// Financeiro → Cadastros → Fornecedores (01/10/2026).
//
// Por que existe: a lista tem ~978 fornecedores, 949 sem CNPJ (97%) e duplicatas
// por nome; até hoje só dava para cadastrar pelo Contas a Pagar, nunca para
// limpar. Esta tela deixa ver quem tem uso, editar, juntar duplicados e apagar
// quem nunca foi usado. Molde: ComercialClientes.tsx.
//
// Carrega tudo de uma vez (escopo=todos, ~1.000 linhas) e filtra no navegador:
// busca e filtros rápidos respondem na hora, sem ida ao servidor a cada tecla.
//
// Duas abas (01/10/2026): "Cadastro" (a lista de sempre) e "Sugestões para
// juntar" (SugestoesJuntar.tsx), que aponta os duplicados prováveis. O default
// export, no fim do arquivo, é o casco com as duas abas.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Table, Input, Button, Space, Flex, Segmented, Tag, Typography, Alert, Badge, Tabs,
  App as AntApp,
} from 'antd';
import {
  SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, MergeCellsOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { usePerms } from '../../hooks/usePerms';
import { fmtDataBr } from '../../utils/dataFmt';
import { formatarCnpjCpf } from '../../utils/fornecedores';
import { ModalNovoFornecedor } from '../financeiro-contas-pagar/ModalNovoFornecedor';
import ModalMesclarFornecedores from './ModalMesclarFornecedores';
import ModalLancamentosFornecedor from './ModalLancamentosFornecedor';
import SugestoesJuntar from './SugestoesJuntar';
import {
  FornecedorLinha, FiltroRapido, Sugestoes, normalizarFornecedor, normalizarSugestoes,
  contarFiltros, filtrarLista, fmtDinheiro, mensagemDeErro, semCnpj,
} from './logica';

const { Text } = Typography;

// `versao` sobe quando a aba de sugestões juntou/descartou algo: a lista daqui
// recarrega sem perder busca nem filtro (a aba fica montada). `onMudou` avisa o
// casco quando ESTA aba muda cadastro (junta, apaga, cria, edita), porque isso
// muda as sugestões — um nome editado pode formar ou desfazer um par.
function CadastroFornecedores({ versao, onMudou }: { versao: number; onMudou: () => void }) {
  const { message, modal } = AntApp.useApp();
  const { pode } = usePerms();
  // Mesmo mecanismo das abas vizinhas (ContasFinanceiras, FinanceiroAvisos):
  // a perm da ação OU admin de permissões. Sem ela o botão some — o servidor
  // recusa de qualquer jeito, mas botão que dá erro é pior que botão ausente.
  const ehAdmin = pode('admin.permissoes.editar');
  const podeEditar = ehAdmin || pode('financeiro.fornecedores.editar');
  const podeExcluir = ehAdmin || pode('financeiro.fornecedores.excluir');

  const [lista, setLista] = useState<FornecedorLinha[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<FiltroRapido>('todos');
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [formAberto, setFormAberto] = useState(false);
  const [editando, setEditando] = useState<FornecedorLinha | null>(null);
  const [verLanc, setVerLanc] = useState<FornecedorLinha | null>(null);
  const [mesclando, setMesclando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga(null);
    try {
      const token = await getToken();
      const r = await axios.get(
        `${API_URL}/fornecedores?escopo=todos&com_uso=1&detalhe=1&limit=5000`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      // 🚨 O portal e a Lambda sobem em jobs separados: por alguns minutos a
      // tela nova pode falar com o servidor velho, que não conhece com_uso e
      // devolve a lista SEM a contagem. Sem esta guarda, todo fornecedor
      // apareceria com 0 lançamentos — e a tela diria que todos podem ser
      // apagados. Resposta degradada tem de se declarar, não se disfarçar.
      if (Array.isArray(d) && d.length > 0 && d[0].lancamentos === undefined) {
        setErroCarga('O servidor ainda está sendo atualizado para esta tela. '
          + 'Espere alguns minutos e clique em "Tentar de novo".');
        setLista([]);
        return;
      }
      const nova = (Array.isArray(d) ? d : []).map(normalizarFornecedor);
      setLista(nova);
      // 🚨 A seleção só guarda quem AINDA existe (revisão adversarial de
      // 01/10/2026). Juntar pela aba Sugestões apaga cadastros sem passar por
      // aqui; sem a poda, o "Juntar (2)" contava um fantasma, o clique não
      // abria nada — e marcar outro depois abria o modal de junção sozinho.
      const existem = new Set(nova.map(f => f.id));
      setSelecionados(sel => sel.filter(id => existem.has(id)));
    } catch (e: any) {
      // Falha de leitura NÃO pode parecer "nenhum fornecedor cadastrado".
      setErroCarga(mensagemDeErro(e, 'Erro ao carregar fornecedores'));
    } finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(); }, [carregar, versao]);

  const contagem = useMemo(() => contarFiltros(lista), [lista]);
  const filtrados = useMemo(() => filtrarLista(lista, filtro, busca), [lista, filtro, busca]);
  // A marcação sobrevive à busca e ao filtro (preserveSelectedRowKeys) de
  // propósito: é assim que se acha um duplicado, marca, procura o outro e
  // marca também. Mas então o botão "Juntar (N)" pode contar quem a tela não
  // está mostrando — e isso precisa estar escrito, não adivinhado.
  const escondidos = useMemo(
    () => selecionados.filter(id => !filtrados.some(f => f.id === id)).length,
    [selecionados, filtrados]);

  function excluir(f: FornecedorLinha) {
    modal.confirm({
      title: `Excluir o fornecedor "${f.nome_fantasia}"?`,
      content: f.lancamentos > 0
        ? `Ele tem ${f.lancamentos} lançamento(s) e o sistema vai recusar a exclusão. Para tirá-lo da lista, use "Juntar" com o cadastro certo.`
        : 'Ele não tem lançamentos. A exclusão não pode ser desfeita.',
      okText: 'Excluir', okType: 'danger', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/financeiro/fornecedores/excluir`, { id: f.id },
            { headers: { Authorization: token } });
          message.success('Fornecedor excluído.');
          setSelecionados(s => s.filter(id => id !== f.id));
          await carregar();
          onMudou();
        } catch (e: any) {
          const frase = mensagemDeErro(e);
          if (e?.response?.status === 409) {
            // A frase do servidor diz quantos lançamentos existem; a saída é juntar.
            modal.warning({
              title: 'Este fornecedor não pode ser excluído',
              content: (
                <div>
                  <p>{frase}</p>
                  <p>Em vez de apagar, marque este fornecedor e o cadastro correto na lista e use <b>Juntar</b>: os lançamentos passam para o que ficar.</p>
                </div>
              ),
              okText: 'Entendi',
            });
          } else {
            message.error('Erro ao excluir: ' + frase, 6);
          }
        }
      },
    });
  }

  const columns: ColumnsType<FornecedorLinha> = [
    {
      title: 'Nome fantasia', dataIndex: 'nome_fantasia', key: 'nf',
      sorter: (a, b) => (a.nome_fantasia || '').localeCompare(b.nome_fantasia || '', 'pt-BR'),
      render: (v) => <Text strong style={{ color: '#020D7F' }}>{v || '—'}</Text>,
    },
    {
      title: 'Razão social', dataIndex: 'razao_social', key: 'rs',
      sorter: (a, b) => (a.razao_social || '').localeCompare(b.razao_social || '', 'pt-BR'),
      render: (v) => v || '—',
    },
    {
      title: 'CNPJ', dataIndex: 'cnpj_cpf', key: 'cnpj', width: 160,
      // Mesma régua do filtro "Sem CNPJ" (semCnpj = nenhum dígito): sem isso a
      // coluna mostrava a pontuação solta de um cadastro que o filtro conta
      // como sem documento, e as duas partes da tela discordavam.
      render: (_v, r) => semCnpj(r)
        ? '—'
        : <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{formatarCnpjCpf(r.cnpj_cpf)}</Text>,
    },
    {
      // Número, não texto: o servidor manda string e "9" viria depois de "80".
      title: 'Lançamentos', dataIndex: 'lancamentos', key: 'lanc', width: 110, align: 'right',
      sorter: (a, b) => a.lancamentos - b.lancamentos,
    },
    {
      title: 'Último lançamento', dataIndex: 'ultimo_lancamento', key: 'ult', width: 130,
      sorter: (a, b) => String(a.ultimo_lancamento || '').localeCompare(String(b.ultimo_lancamento || '')),
      render: (v) => fmtDataBr(v),
    },
    {
      // "lançado", não "pago": a soma é de tudo o que entrou no Contas a Pagar
      // para este fornecedor, inclusive o que ainda está em aberto.
      title: 'Total lançado', dataIndex: 'total_pago', key: 'total', width: 140, align: 'right',
      sorter: (a, b) => a.total_pago - b.total_pago,
      render: (v: number) => v ? fmtDinheiro(v) : '—',
    },
    {
      title: 'Situação', dataIndex: 'ativo', key: 'sit', width: 90,
      render: (v: boolean) => v ? null : <Tag>Inativo</Tag>,
    },
    {
      title: '', key: 'acoes', width: 120, align: 'center',
      render: (_, r) => (
        <Space size={2}>
          {podeEditar && (
            <Button size="small" type="text" icon={<EditOutlined />} title="Editar"
              onClick={() => { setEditando(r); setFormAberto(true); }} />
          )}
          <Button size="small" type="text" icon={<EyeOutlined />} title="Ver os lançamentos"
            onClick={() => setVerLanc(r)} />
          {podeExcluir && (
            <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Excluir"
              onClick={() => excluir(r)} />
          )}
        </Space>
      ),
    },
  ];

  const paraJuntar = lista.filter(f => selecionados.includes(f.id));
  // O modal só existe com 2+ cadastros. Se o pedido de juntar ficou ligado sem
  // eles, desliga — senão o modal (que apaga cadastro) abriria sozinho mais tarde.
  useEffect(() => {
    if (mesclando && paraJuntar.length < 2) setMesclando(false);
  }, [mesclando, paraJuntar.length]);

  return (
    <div style={{ padding: '16px 24px' }}>
      <Flex gap={12} align="center" style={{ marginBottom: 12 }} wrap="wrap">
        <Input
          placeholder="Buscar por nome, razão social ou CNPJ..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          prefix={<SearchOutlined style={{ color: '#aaa' }} />}
          allowClear
          style={{ maxWidth: 400, flex: 1, minWidth: 220 }}
        />
        {podeExcluir && selecionados.length >= 2 && (
          <Button danger icon={<MergeCellsOutlined />} onClick={() => setMesclando(true)}>
            Juntar ({selecionados.length}){escondidos > 0 ? ` · ${escondidos} fora da lista` : ''}
          </Button>
        )}
        {selecionados.length > 0 && (
          <Button onClick={() => setSelecionados([])}>Limpar seleção</Button>
        )}
        {podeEditar && (
          <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }}
            onClick={() => { setEditando(null); setFormAberto(true); }}>
            Novo fornecedor
          </Button>
        )}
      </Flex>

      <Segmented
        style={{ marginBottom: 12 }}
        value={filtro}
        onChange={v => setFiltro(v as FiltroRapido)}
        options={[
          { value: 'todos', label: `Todos (${contagem.todos})` },
          { value: 'sem_cnpj', label: `Sem CNPJ (${contagem.sem_cnpj})` },
          { value: 'sem_lancamentos', label: `Sem lançamentos (${contagem.sem_lancamentos})` },
          { value: 'inativos', label: `Inativos (${contagem.inativos})` },
        ]}
      />

      {erroCarga && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }}
          message="Não foi possível carregar os fornecedores" description={erroCarga}
          action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />
      )}

      {podeExcluir && selecionados.length >= 1 && (
        <Alert type="info" showIcon style={{ marginBottom: 12, fontSize: 12 }}
          message={`${selecionados.length} fornecedor(es) selecionado(s)`
            + (escondidos > 0 ? ` — ${escondidos} fora do que a lista mostra agora` : '')}
          description={selecionados.length >= 2
            ? "Clique em 'Juntar' para escolher qual manter — os lançamentos dos outros passam para ele."
            : 'Marque outro para juntar os dois. A marcação sobrevive à busca e ao filtro, então dá para achar um, marcar, e depois achar o outro.'} />
      )}

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtrados}
        loading={carregando}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `${t} fornecedor(es)` }}
        // Só quem pode excluir enxerga a seleção: ela serve apenas para o Juntar.
        rowSelection={podeExcluir ? {
          selectedRowKeys: selecionados,
          // Mantém a escolha ao mudar a busca/filtro: dá para marcar um duplicado,
          // procurar o outro e juntar os dois.
          preserveSelectedRowKeys: true,
          onChange: (keys) => setSelecionados(keys as string[]),
        } : undefined}
        locale={{ emptyText: carregando || erroCarga ? ' '
          : lista.length === 0 ? 'Nenhum fornecedor cadastrado.' : 'Nenhum resultado.' }}
        scroll={{ x: 'max-content' }}
      />

      {formAberto && (
        <ModalNovoFornecedor
          fornecedor={editando || undefined}
          comObservacoes
          onFechar={() => { setFormAberto(false); setEditando(null); }}
          onCriou={() => { setFormAberto(false); setEditando(null); carregar(); onMudou(); }}
        />
      )}

      {verLanc && (
        <ModalLancamentosFornecedor fornecedor={verLanc} onFechar={() => setVerLanc(null)} />
      )}

      {mesclando && paraJuntar.length >= 2 && (
        <ModalMesclarFornecedores
          fornecedores={paraJuntar}
          onFechar={() => setMesclando(false)}
          onSalvou={() => { setMesclando(false); setSelecionados([]); carregar(); onMudou(); }}
        />
      )}
    </div>
  );
}

export default function FinanceiroFornecedores() {
  const { pode } = usePerms();
  // Mesmo critério do botão Juntar da aba Cadastro.
  const podeExcluir = pode('admin.permissoes.editar') || pode('financeiro.fornecedores.excluir');
  const [sp, setSp] = useSearchParams();
  // A aba escolhida vai na URL (?sub=sugestoes) para o link e o F5 voltarem
  // nela. Copia os outros params: o ?aba=fornecedores é do hub, e apagá-lo
  // jogaria a pessoa para a primeira aba do hub.
  const sub = sp.get('sub') === 'sugestoes' ? 'sugestoes' : 'cadastro';
  const trocar = (key: string) => {
    const next = new URLSearchParams(sp);
    if (key === 'sugestoes') next.set('sub', 'sugestoes'); else next.delete('sub');
    setSp(next, { replace: true });
  };

  // As sugestões moram aqui, e não na aba, porque o número ao lado do nome da
  // aba tem de aparecer mesmo com o Cadastro aberto.
  const [sugestoes, setSugestoes] = useState<Sugestoes | null>(null);
  const [carregandoSug, setCarregandoSug] = useState(true);
  const [erroSug, setErroSug] = useState<string | null>(null);
  const [versaoCadastro, setVersaoCadastro] = useState(0);

  // Só a ÚLTIMA carga pedida grava o estado (revisão adversarial de 01/10/2026):
  // descartar e logo "Desfazer" disparam duas cargas, e se a primeira voltasse
  // por último a tela mostraria a lista velha — sem o grupo que acabou de voltar.
  const seqSug = useRef(0);
  const carregarSugestoes = useCallback(async () => {
    const minha = ++seqSug.current;
    setCarregandoSug(true);
    setErroSug(null);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/financeiro/fornecedores/sugestoes`,
        { headers: { Authorization: token } });
      if (minha !== seqSug.current) return;
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      const norm = normalizarSugestoes(d);
      // Resposta sem a lista de grupos (servidor ainda velho, por exemplo) tem
      // de virar erro: se virasse lista vazia, a tela diria "nenhuma sugestão".
      if (!norm) {
        setErroSug('O servidor ainda está sendo atualizado para esta tela. '
          + 'Espere alguns minutos e clique em "Tentar de novo".');
        return;
      }
      setSugestoes(norm);
    } catch (e: any) {
      if (minha !== seqSug.current) return;
      setErroSug(mensagemDeErro(e, 'Erro ao carregar as sugestões'));
    } finally { if (minha === seqSug.current) setCarregandoSug(false); }
  }, []);

  useEffect(() => { carregarSugestoes(); }, [carregarSugestoes]);

  // Depois de juntar ou descartar, os números das DUAS abas mudam.
  const aposMudanca = useCallback(() => {
    carregarSugestoes();
    setVersaoCadastro(v => v + 1);
  }, [carregarSugestoes]);

  const nGrupos = sugestoes?.grupos.length ?? 0;

  return (
    <Tabs
      activeKey={sub}
      onChange={trocar}
      // Sem destruir a aba inativa: busca, filtro e seleção do Cadastro
      // sobrevivem a uma ida às sugestões.
      tabBarStyle={{ margin: 0, padding: '0 24px' }}
      items={[
        {
          key: 'cadastro',
          label: 'Cadastro',
          children: <CadastroFornecedores versao={versaoCadastro} onMudou={carregarSugestoes} />,
        },
        {
          key: 'sugestoes',
          label: (
            <Space size={6}>
              Sugestões para juntar
              {nGrupos > 0 && <Badge count={nGrupos} overflowCount={999} color="#0930D0" />}
            </Space>
          ),
          children: (
            <SugestoesJuntar
              dados={sugestoes}
              carregando={carregandoSug}
              erro={erroSug}
              podeExcluir={podeExcluir}
              onTentarDeNovo={carregarSugestoes}
              onMudou={aposMudanca}
            />
          ),
        },
      ]}
    />
  );
}
